import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCustomerDetails, getClientList, updateCustomerDetails } from "./CustomerView.actions";
import { getSensitiveFields } from "../ClientView/ClientView.actions";
import { CUSTOMER_UPDATE_SENSITIVE_FIELDS } from "../../../lookup/CustomerViewConstants";
import Spinner from "components/Spinner";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import { ColumnTypes } from "commons/table/Constants";
import { FilterTypes } from "commons/table/Constants";
import './CustomerView.css';
import { DEFAULT_PAGE_SIZE } from "../CustomerList/Customer.reducer";
import CreateClientModal from "./CreateClientModal";
import ConfirmationModal from "components/ConfirmationModal";
import { FaEye } from "react-icons/fa";
import { formatPhoneNumber } from "services/UtilityService";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";

const CustomerView = () => {
  const { customerInfoUuid } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    details: customer,
    loadingDetails,
    clients,
    loadingClients,
    clientListState,
    pages,
    totalElements,
    updating,
    sensitiveFields,
  } = useSelector(
    (state) => state.customerView
  );

  const [formState, setFormState] = useState(null);
  const [originalFormState, setOriginalFormState] = useState(null);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const canManage = hasRoleIn([PERMISSIONS.PERM_CONFIG_TOOL_MANAGE]);
  const isReadOnly = !canManage;

  useEffect(() => {
    dispatch(getCustomerDetails(customerInfoUuid));
    dispatch(getClientList(customerInfoUuid, clientListState.pageSize, clientListState.page, clientListState.sorted, clientListState.filtered));
    dispatch(getSensitiveFields(CUSTOMER_UPDATE_SENSITIVE_FIELDS));
  }, [dispatch, customerInfoUuid]);

  useEffect(() => {
    if (customer) {
      setFormState({
        ...customer,
        supportNumber: customer.supportNumber ? customer.supportNumber.replace(/[^0-9]/g, '').slice(0, 10) : '',
        faxNumberInbound: customer.faxNumberInbound ? customer.faxNumberInbound.replace(/[^0-9]/g, '').slice(0, 10) : '',
        faxNumberOutbound: customer.faxNumberOutbound ? customer.faxNumberOutbound.replace(/[^0-9]/g, '').slice(0, 10) : '',
        showSavingPrices: customer.showSavingPrices !== undefined ? customer.showSavingPrices : true,
      });
      setOriginalFormState({ ...customer });
    }
  }, [customer]);

  const onUpdateClientTable = (filters, sort, pageSize, page) => {
    dispatch(getClientList(customerInfoUuid, pageSize, page, sort, filters));
  };

  const handleClientCreated = () => {
    onUpdateClientTable(clientListState.filtered, clientListState.sorted, clientListState.pageSize, clientListState.page);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    let newValue = value;

    if (['supportNumber', 'faxNumberInbound', 'faxNumberOutbound'].includes(name)) {
      // Only allow digits
      newValue = value.replace(/[^0-9]/g, '').slice(0, 10);
    }

    setFormState(prevState => ({
      ...prevState,
      [name]: newValue
    }));
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setFormState(prevState => ({
      ...prevState,
      [name]: checked
    }));
  };

  const handleCancel = () => {
    navigate("/admin/customers");
  };

  const proceedWithSave = () => {
    dispatch(updateCustomerDetails(customerInfoUuid, formState)).then(
      (response) => {
        if (response && response.type === "UPDATE_CUSTOMER_DETAILS_SUCCESS") {
          navigate("/admin/customers");
        }
      }
    );
  };

  const handleSave = async () => {
    const changedFields = sensitiveFields.filter(field => originalFormState[field] !== formState[field]);
    if (changedFields.length > 0) {
      setShowConfirmationModal(true);
    } else {
      proceedWithSave();
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmationModal(false);
    proceedWithSave();
  };

  const clientColumns = [
    {
      header: "Name",
      accessor: "clientName",
      accessorKey: "clientName",
      enableSorting: true,
      enableColumnFilter: true,
      ColumnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Short Code",
      accessor: "clientNameAbbreviation",
      accessorKey: "clientNameAbbreviation",
      enableSorting: true,
      enableColumnFilter: true,
      ColumnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Actions",
      accessor: "action",
      accessorKey: "action",
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value, row) => {
        return (
          <Link variant="primary" to={`/admin/client/${row.original.clientInfoUuid}`}>
            <FaEye /> &nbsp; View
          </Link>
        );
      },
    },
  ];

  const isFieldSensitive = (fieldName) => {
    return sensitiveFields.includes(fieldName);
  };

  const renderLabel = (label, fieldName) => {
    return (
      <Form.Label>
        {label}
        {isFieldSensitive(fieldName) && (
          <span className="badge badge-warning ml-2" style={{ fontSize: '0.7em', verticalAlign: 'middle' }}>Sensitive</span>
        )}
      </Form.Label>
    );
  };

  const renderSwitch = (label, fieldName, checked, onChange, id) => {
    return (
      <Form.Group>
        <div className="d-flex align-items-center">
            <Form.Check
              type="switch"
              id={id}
              label={label}
              name={fieldName}
              checked={checked}
              onChange={onChange}
              disabled={isReadOnly}
            />
            {isFieldSensitive(fieldName) && (
              <span className="badge badge-warning ml-2" style={{ fontSize: '0.7em' }}>Sensitive</span>
            )}
        </div>
      </Form.Group>
    );
  };

  if (loadingDetails || !customer || !formState) {
    return <Spinner />;
  }

  return (
    <Container fluid>
      <ConfirmationModal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirmSave}
        title="Confirm Save"
        body="You have modified sensitive fields. Are you sure you want to save these changes?"
      />
      <Row>
        <Col md={12}>
          <h5>{customer.customerName}</h5>
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mt-4">
          <div className="customer-details-wrapper">
            {updating && (
              <div className="updating-overlay">
                <Spinner />
              </div>
            )}
            <Card>
              <Card.Header>
                <Card.Title as="h6">Identity</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Customer Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" value={formState.customerName} disabled />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Program Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" value={formState.programName} disabled/>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Group>
                      <Form.Label>Short Code <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" value={formState.customerNameAbbreviation} disabled/>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                      <Form.Group>
                      {renderLabel("Logo URL", "logoUrl")}
                      <Form.Control type="text" name="logoUrl" value={formState.logoUrl} onChange={handleInputChange} disabled={isReadOnly} />
                    </Form.Group>
                  </Col>
                  {/* <Col md={6} className="mt-2">
                    <Form.Group>
                      <Form.Label>Default Fax Template</Form.Label>
                      <Form.Control type="text" value={formState.defaultFaxTemplate} />
                    </Form.Group>
                  </Col> */}
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </Col>
        <Col md={12} className="mt-4">
          <div className="customer-details-wrapper">
            <Card>
              <Card.Header>
                <Card.Title as="h6">Contact</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                  <Col md={6}>
                    <Form.Group>
                      {renderLabel("Support Number", "supportNumber")}
                      <Form.Control type="text" name="supportNumber" value={formatPhoneNumber(formState.supportNumber)} onChange={handleInputChange} placeholder="e.g. (555) 555-5555" maxLength={14} disabled={isReadOnly} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      {renderLabel("Inbound Fax Number", "faxNumberInbound")}
                      <Form.Control type="text" name="faxNumberInbound" value={formatPhoneNumber(formState.faxNumberInbound)} onChange={handleInputChange} placeholder="e.g. (555) 555-5555" maxLength={14} disabled={isReadOnly} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Group>
                      {renderLabel("Outbound Fax Number", "faxNumberOutbound")}
                      <Form.Control type="text" name="faxNumberOutbound" value={formatPhoneNumber(formState.faxNumberOutbound)} onChange={handleInputChange} placeholder="e.g. (555) 555-5555" maxLength={14} disabled={isReadOnly} />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Group>
                      {renderLabel("Email", "email")}
                      <Form.Control type="email" name="email" value={formState.email} onChange={handleInputChange} disabled={isReadOnly} />
                    </Form.Group>
                  </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col md={12}>
          <div className="customer-details-wrapper">
            <Card>
              <Card.Header>
                <Card.Title as="h6">Flags</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                  <Col md={6}>
                    {renderSwitch(
                      "Auto Accept Enabled",
                      "isAutoAcceptEnabled",
                      formState.isAutoAcceptEnabled,
                      handleCheckboxChange,
                      "auto-accept-switch"
                    )}
                  </Col>
                  <Col md={6}>
                     {renderSwitch(
                      "Intro Fax Enabled",
                      "isIntroFaxEnabled",
                      formState.isIntroFaxEnabled,
                      handleCheckboxChange,
                      "intro-fax-switch"
                    )}
                  </Col>
                  </Row>
                  <Row className="mt-2">
                  <Col md={6}>
                    {renderSwitch(
                      "Show Saving Prices",
                      "showSavingPrices",
                      formState.showSavingPrices,
                      handleCheckboxChange,
                      "show-saving-prices-switch"
                    )}
                  </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
      {canManage && (
        <Row className="mt-4">
          <Col md={12} className="text-right">
            <Button variant="secondary" onClick={handleCancel} className="mr-2">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={updating} className="save-button">
              Save
            </Button>
          </Col>
        </Row>
      )}
      <Row className="mt-4">
        <Col md={12}>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <Card.Title as="h6" className="mb-0">Clients {loadingClients ? "" : "(" + totalElements +")" } </Card.Title>
                {canManage && (
                  <Button variant="primary" size="sm" onClick={() => setShowCreateClientModal(true)}>
                    Create Client
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              <ServerSideReactTable
                columnDefination={clientColumns}
                data={clients}
                loading={loadingClients}
                currentPage={clientListState.page}
                totalPages={pages}
                pageSize={clientListState.pageSize}
                defaultPageSize={DEFAULT_PAGE_SIZE}
                totalRecords={totalElements}
                sorted={clientListState.sorted}
                filters={clientListState.filtered}
                onUpdateTableData={onUpdateClientTable}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <CreateClientModal
        show={showCreateClientModal}
        onHide={() => setShowCreateClientModal(false)}
        customerInfoUuid={customerInfoUuid}
        customerName={customer?.customerName}
        onSuccess={handleClientCreated}
      />
    </Container>
  );
};

export default CustomerView;